// JobsiteVisits Service
// Following patterns from existing JobsiteService

import { api } from "../intreceptor/apiInterceptor";
import { API_ENDPOINTS } from "./api.endpoints";
import {
  JobsiteVisitUpdateDTO,
  JobsiteVisitDTO,
  JobsiteVisitWorkersDTO,
  CreateJobsiteVisitsTradeDTO,
  JobsiteVisitFilesDTO,
  JobsiteVisitResponse,
  JobsiteVisitListResponse,
  LatestWorkersResponse,
  JobsiteVisitFilters,
  JobsiteVisitsApiResponse,
  JobsiteVisitsByJobIdParams
} from "../types/jobsite-visits.types";

export const JobsiteVisitsService = {
  /**
   * Create a new jobsite visit (flat structure)
   */
  async createJobsiteVisit(
    visitData: JobsiteVisitDTO,
  ): Promise<JobsiteVisitResponse> {

    return api.fetch('/jobsite-visits/create-jobvisit', {
      method: 'POST',
      body: JSON.stringify(visitData)
    });
  },

  /**
   * Create jobsite visit files (matching jobsite creation pattern)
   */
  async createJobsiteVisitFiles(filesData: JobsiteVisitFilesDTO): Promise<any> {
    const formData = new FormData();
    formData.append('jobsiteVisitId', filesData.jobsiteVisitId || '');
    formData.append('userId', filesData.userId);

    filesData.files.forEach((file) => {
      formData.append('files', file);
    });

    return await api.fetch("/jobsite-visits/jobsite-files", {
      method: 'POST',
      body: formData,
      isFormData: true
    });
  },

  /**
   * Create workers data for a jobsite visit
   */
  async createJobsiteVisitWorkers(workersData: JobsiteVisitWorkersDTO): Promise<JobsiteVisitsApiResponse<any>> {
    return api.fetch("/jobsite-visits/jobsite-workers", {
      method: 'POST',
      body: JSON.stringify(workersData)
    });
  },

  /**
   * Create trades data for a jobsite visit
   */
  async createJobsiteVisitTrades(tradesData: CreateJobsiteVisitsTradeDTO): Promise<JobsiteVisitsApiResponse<any>> {
    return api.fetch("/jobsite-visits/jobsite-trades", {
      method: 'POST',
      body: JSON.stringify(tradesData)
    });
  },


  /**
   * Get all jobsite visits with optional filters
   */
  async getAllJobsiteVisits(filters: JobsiteVisitFilters = {}): Promise<JobsiteVisitListResponse[]> {
    const params = new URLSearchParams();

    // Handle array parameters
    ['status', 'contractor'].forEach(key => {
      const value = filters[key as keyof JobsiteVisitFilters];
      if (Array.isArray(value) && value.length > 0) {
        value.forEach((item: string) => {
          params.append(key, item);
        });
      }
    });

    // Handle single parameters
    if (filters.jobId) params.append('jobId', filters.jobId);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const queryString = params.toString().replace(/\+/g, '%20');
    const endpoint = queryString
      ? `${API_ENDPOINTS.JOBSITE_VISITS.GET_VISIT_BY_ID}?${queryString}`
      : API_ENDPOINTS.JOBSITE_VISITS.GET_VISIT_BY_ID;

    return api.fetch(endpoint, {
      method: 'GET'
    });
  },

  /**
   * Get a specific jobsite visit by ID with all related data
   */
  async getJobsiteVisitById(visitId: string): Promise<JobsiteVisitResponse> {
    return api.fetch(`/jobsite-visits/get-jobsite-visit/${visitId}`, {
      method: 'GET'
    });
  },

  /**
   * Get all jobsite visits by job id
   */
  async getJobsiteVisitRecordsByJobId(jobId: string, params: JobsiteVisitsByJobIdParams): Promise<{ data: JobsiteVisitListResponse[], totalCount: number }> {
    const url = new URL(`/jobsite-visits/get-jobsite-visits/${jobId}`, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => url.searchParams.append(key, v));
        } else if (value !== undefined) {
          url.searchParams.append(key, value.toString());
        }
      });
    }

    return api.fetch(url.pathname + url.search, { method: 'GET' });
  },

  /**
   * Get latest workers data from all jobsites for prepopulation
   */
  async getLatestWorkersData(): Promise<LatestWorkersResponse[]> {
    return api.fetch(API_ENDPOINTS.JOBSITE_VISITS.GET_LATEST_WORKERS, {
      method: 'GET'
    });
  },

  /**
   * Get latest workers data for a specific jobsite
   */
  async getLatestWorkersByJobId(jobId: string): Promise<LatestWorkersResponse | null> {
    const allLatestWorkers = await this.getLatestWorkersData();
    return allLatestWorkers.find(item => item.jobId === jobId) || null;
  },

  /**
   * Upload files for a jobsite visit
   */
  async uploadJobsiteVisitFiles(filesData: JobsiteVisitFilesDTO): Promise<JobsiteVisitsApiResponse<any>> {
    const formData = new FormData();
    formData.append('jobsiteVisitId', filesData.jobsiteVisitId || '');
    formData.append('userId', filesData.userId);

    filesData.files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      return await api.fetch(API_ENDPOINTS.JOBSITE_VISITS.UPLOAD_FILES, {
        method: 'POST',
        body: formData,
        isFormData: true
      });
    } catch (error) {
      console.error('Error uploading jobsite visit files:', error);
      throw error;
    }
  },

  /**
   * Update an existing jobsite visit
   */
  async updateJobsiteVisit(visitId: string, updateData: JobsiteVisitUpdateDTO): Promise<JobsiteVisitsApiResponse<JobsiteVisitResponse>> {
    return api.fetch(`/jobsite-visits/update-jobsite-visit/${visitId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData)
    });
  },

  /**
   * Delete a jobsite visit (soft delete)
   */
  async deleteJobsiteVisit(visitId: string, userId: string, reason: string): Promise<JobsiteVisitsApiResponse<{ message: string }>> {
    return api.fetch(`${API_ENDPOINTS.JOBSITE_VISITS.CREATE_VISIT}/${visitId}`, {
      method: 'DELETE',
      body: JSON.stringify({ userId, reason })
    });
  },

  /**
   * Delete a single file from a jobsite visit (soft delete, admin only)
   */
  async deleteJobsiteVisitFile(fileId: string): Promise<any> {
    return api.fetch(`/jobsite-visits/files/${fileId}`, {
      method: 'DELETE',
      requiresAuth: true
    });
  },

  /**
   * Get files for a specific jobsite visit
   */
  async getJobsiteVisitFiles(jobsiteVisitId: string): Promise<any[]> {
    return api.fetch(`/api/jobsite-visits/files/${jobsiteVisitId}`, {
      method: 'GET'
    });
  },

  /**
   * Get trades for a specific jobsite visit
   */
  async getJobsiteVisitTrades(jobsiteVisitId: string): Promise<any[]> {
    return api.fetch(`/api/jobsite-visits/trades/${jobsiteVisitId}`, {
      method: 'GET'
    });
  },

  /**
   * Update jobsite visit workers
   */
  async updateJobsiteVisitWorkers(
    visitId: string,
    workersData: JobsiteVisitWorkersDTO
  ): Promise<any> {
    return api.fetch(`/jobsite-visits/update-jobsite-workers/${visitId}`, {
      method: 'PATCH',
      body: JSON.stringify(workersData)
    });
  },

  /**
   * Update jobsite visit trades
   */
  async updateJobsiteVisitTrades(
    visitId: string,
    tradesData: CreateJobsiteVisitsTradeDTO
  ): Promise<any> {
    return api.fetch(`/jobsite-visits/update-jobsite-trades/${visitId}`, {
      method: 'PATCH',
      body: JSON.stringify(tradesData)
    });
  },

  /**
   * Create a comment for a jobsite visit
   */
  async createJobsiteVisitComment(data: {
    comment: string;
    userId: string;
    jobsiteVisitId: string;
  }): Promise<any> {
    try {
      const response = await api.fetch('/jobsite-visit-comments/create', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      console.error('Error creating jobsite visit comment:', error);
      throw error;
    }
  },

  /**
   * Get all comments for a jobsite visit
   */
  async getCommentsByVisitId(jobsiteVisitId: string): Promise<any[]> {
    try {
      const response: any = await api.fetch(
        `/jobsite-visit-comments/getComments/${jobsiteVisitId}`,
        {
          method: 'GET',
        }
      );

      // Handle empty response
      if (!response.success && response.message) {
        return [];
      }

      // Ensure response is an array
      if (Array.isArray(response)) {
        return response;
      }

      return [];
    } catch (error) {
      console.error('Error fetching jobsite visit comments:', error);
      return [];
    }
  },

  /**
   * Update a comment for a jobsite visit
   */
  async updateJobsiteVisitComment(commentId: string, comment: string, userId: string): Promise<any> {
    try {
      console.log('🌐 API Call: Updating comment', { commentId, comment, userId });
      const response = await api.fetch(`/jobsite-visit-comments/update/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ comment, userId }),
      });
      console.log('📡 API Response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error updating jobsite visit comment:', error);
      throw error;
    }
  },

};

export default JobsiteVisitsService;